import { useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  EditableToken,
} from '@vibesites/runtime/react';
import {
  GalleryRow,
  GalleryLightbox,
  GallerySharedStyles,
  useGalleryHeadingColor,
  type GalleryImage,
} from './_galleryShared';

/**
 * Apartment-side of the photo gallery — renders the "Interior Luxuries"
 * grid (with its own lightbox). Photos are selected via the collection
 * binding's exact-match `filter` on `title === 'Apartment'`, so the
 * property's photo_gallery must categorize apartment shots with that
 * exact title.
 */
export function GalleryApartments() {
  const heading = useContentToken('galleryapartments.heading');
  const headingColor = useGalleryHeadingColor();

  const apartmentImages = useCollectionBinding<GalleryImage>('galleryapartments.images', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (apartmentImages.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <GalleryRow
          heading={
            <EditableToken
              path="galleryapartments.heading"
              as="h2"
              className="gallery-row__heading"
              style={{ color: headingColor }}
            >
              {heading}
            </EditableToken>
          }
          images={apartmentImages}
          onSelect={setSelectedIndex}
        />
      </div>

      <GalleryLightbox
        images={apartmentImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryApartments',
  displayName: 'Apartment Gallery',
  tokens: {
    'galleryapartments.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Interior Luxuries',
    },
  },
  collectionBindings: {
    'galleryapartments.images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Apartment" appear in this grid.',
    },
  },
};

(GalleryApartments as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryApartments;
